import { jmapGatewayClient } from './apiClient';
import type {
  Contact,
  ContactGroup,
  CreateContactRequest,
  UpdateContactRequest,
  CreateContactGroupRequest,
  UpdateContactGroupRequest,
} from '@/types/api';

/**
 * Contacts API client for server-backed personal contacts
 */
class ContactsClient {
  // ========== Contact CRUD ==========

  async listContacts(token: string): Promise<Contact[]> {
    return await jmapGatewayClient.get<Contact[]>('/contacts', token);
  }

  async getContact(token: string, contactId: string): Promise<Contact> {
    return await jmapGatewayClient.get<Contact>(`/contacts/${contactId}`, token);
  }

  async createContact(token: string, contact: CreateContactRequest): Promise<Contact> {
    return await jmapGatewayClient.post<Contact>('/contacts', token, contact);
  }

  async updateContact(token: string, contactId: string, updates: UpdateContactRequest): Promise<Contact> {
    return await jmapGatewayClient.patch<Contact>(`/contacts/${contactId}`, token, updates);
  }

  async deleteContact(token: string, contactId: string): Promise<void> {
    return await jmapGatewayClient.delete(`/contacts/${contactId}`, token);
  }

  async searchContacts(token: string, query: string): Promise<Contact[]> {
    return await jmapGatewayClient.get<Contact[]>('/contacts/search', token, { params: { q: query } });
  }

  async getFavoriteContacts(token: string): Promise<Contact[]> {
    return await jmapGatewayClient.get<Contact[]>('/contacts/favorites', token);
  }

  // ========== Contact Groups ==========

  async listGroups(token: string): Promise<ContactGroup[]> {
    return await jmapGatewayClient.get<ContactGroup[]>('/contacts/groups', token);
  }

  async getGroup(token: string, groupId: string): Promise<ContactGroup> {
    return await jmapGatewayClient.get<ContactGroup>(`/contacts/groups/${groupId}`, token);
  }

  async createGroup(token: string, group: CreateContactGroupRequest): Promise<ContactGroup> {
    return await jmapGatewayClient.post<ContactGroup>('/contacts/groups', token, group);
  }

  async updateGroup(token: string, groupId: string, updates: UpdateContactGroupRequest): Promise<ContactGroup> {
    return await jmapGatewayClient.patch<ContactGroup>(`/contacts/groups/${groupId}`, token, updates);
  }

  async deleteGroup(token: string, groupId: string): Promise<void> {
    return await jmapGatewayClient.delete(`/contacts/groups/${groupId}`, token);
  }

  async getGroupMembers(token: string, groupId: string): Promise<Contact[]> {
    return await jmapGatewayClient.get<Contact[]>(`/contacts/groups/${groupId}/members`, token);
  }

  async addContactToGroup(token: string, groupId: string, contactId: string): Promise<void> {
    return await jmapGatewayClient.post(`/contacts/groups/${groupId}/members/${contactId}`, token, {});
  }

  async removeContactFromGroup(token: string, groupId: string, contactId: string): Promise<void> {
    return await jmapGatewayClient.delete(`/contacts/groups/${groupId}/members/${contactId}`, token);
  }
}

export const contactsClient = new ContactsClient();
